/**
 * Chatbot hỗ trợ đương sự – nút nổi, mẫu câu chọn sẵn chạy theo scenario.
 */
import { useState, useRef, useEffect } from 'react'
import {
  Box,
  Typography,
  IconButton,
  Fab,
  Paper,
  TextField,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Chip,
} from '@mui/material'
import { Chat as ChatIcon, Send, Close } from '@mui/icons-material'

type Message = { id: string; from: 'bot' | 'user'; text: string }

const INIT_MESSAGES: Message[] = [
  { id: '1', from: 'bot', text: 'Xin chào! Tôi là trợ lý ảo của Tòa án điện tử. Chọn một chủ đề bên dưới để xem hướng dẫn từng bước.' },
]

/** Scenario: khi chọn mẫu → gửi câu user rồi trả lời bot (có thể nhiều bước). */
type ScenarioStep = { bot: string }
interface Scenario {
  id: string
  label: string
  userMessage: string
  steps: ScenarioStep[]
}

const SCENARIOS: Scenario[] = [
  {
    id: 'nop-don',
    label: 'Nộp đơn khởi kiện',
    userMessage: 'Làm sao để nộp đơn khởi kiện?',
    steps: [
      { bot: 'Bạn có thể nộp đơn khởi kiện trực tuyến như sau:\n\n1. Vào menu **Nộp đơn khởi kiện** (hoặc nút trên trang Vụ án của tôi).\n2. Chọn phân luồng (Track A: fully online nếu giá trị < 200 triệu; Track B: hybrid).\n3. Điền thông tin các bên, loại tranh chấp, nội dung và tải chứng cứ đính kèm.\n4. Dùng gợi ý AI để điền nhanh và chọn chứng cứ cần nộp theo loại vụ.\n5. Kiểm tra và bấm **Gửi đơn**.\n\nSau khi gửi, đơn sẽ vào giai đoạn thẩm tra; bạn có thể theo dõi tại **Vụ án của tôi**.' },
    ],
  },
  {
    id: 'theo-doi-vu',
    label: 'Theo dõi tiến trình vụ án',
    userMessage: 'Vụ án của tôi đang ở bước nào? Làm sao xem?',
    steps: [
      { bot: 'Để xem vụ án đang ở bước nào:\n\n1. Vào **Vụ án của tôi** trên menu.\n2. Chọn vụ án cần xem (bấm **Chi tiết**).\n3. Trong trang chi tiết, xem mục **Tiến trình vụ án** — mỗi mốc (thụ lý, phân công, phiên họp, quyết định...) sẽ hiển thị theo thời gian.\n4. Phần **Văn bản tố tụng** cho biết giấy tờ đã gửi (giấy báo, thông báo phiên...).\n\nTrạng thái tổng thể (Đã nộp, Đã thụ lý, Chuẩn bị xét xử, Đang xét xử, Đã kết thúc) hiển thị ngay trên đầu trang.' },
    ],
  },
  {
    id: 'khang-cao',
    label: 'Thời hạn và thủ tục kháng cáo',
    userMessage: 'Thời hạn kháng cáo là bao lâu? Nộp đơn kháng cáo ở đâu?',
    steps: [
      { bot: '**Thời hạn kháng cáo:** Theo quy định, đương sự có quyền kháng cáo bản án sơ thẩm trong thời hạn **15 ngày** kể từ ngày tuyên án (người không tham gia phiên có thể tính từ ngày nhận được bản án).\n\n**Cách nộp đơn kháng cáo:**\n1. Vào **Vụ án của tôi** → chọn vụ đã có bản án.\n2. Trong phần **Bản án & Quyền kháng cáo**, bấm **Nộp đơn kháng cáo**.\n3. Điền lý do và gửi qua hệ thống. Hồ sơ sẽ được chuyển Tòa cấp trên để xét xử phúc thẩm.' },
    ],
  },
  {
    id: 'giay-bao-phi',
    label: 'Giấy báo nộp tạm ứng án phí',
    userMessage: 'Tôi nhận được giấy báo nộp tạm ứng án phí, cần làm gì?',
    steps: [
      { bot: 'Khi nhận **Giấy báo nộp tạm ứng án phí**:\n\n1. Vào **Vụ án của tôi** → chọn vụ án tương ứng.\n2. Trong **Văn bản tố tụng**, tìm văn bản “Giấy báo nộp tạm ứng án phí” và bấm **Xem**.\n3. Thực hiện nộp tạm ứng án phí theo hướng dẫn trên giấy (kênh ngân hàng / cổng thanh toán).\n4. Sau khi đã nộp, quay lại trang vụ án và bấm **Xác nhận đã nhận** rồi **Đã đóng phí** để cập nhật trạng thái.\n\nNếu có khiếu nại về số tiền hoặc thủ tục, dùng nút **Khiếu nại** và gửi nội dung qua hệ thống.' },
    ],
  },
  {
    id: 'xin-hoan-phiên',
    label: 'Xin hoãn phiên / đổi lịch',
    userMessage: 'Tôi muốn xin hoãn phiên hoặc đổi lịch họp.',
    steps: [
      { bot: 'Để **xin hoãn phiên** hoặc **yêu cầu thay đổi lịch**:\n\n1. Vào **Vụ án của tôi** → chọn vụ án có phiên sắp tới.\n2. Trong trang chi tiết, tìm khung **Phiên sắp tới** (hiển thị ngày giờ phiên).\n3. Bấm **Xin hoãn phiên** hoặc **Yêu cầu thay đổi lịch**.\n4. Nhập lý do (bắt buộc) và gửi. Tòa sẽ xem xét và thông báo kết quả qua hệ thống.\n\nLưu ý: Nên gửi sớm trước ngày phiên để Tòa kịp xử lý.' },
    ],
  },
  {
    id: 'van-ban-da-nhan',
    label: 'Xác nhận đã nhận văn bản',
    userMessage: 'Làm sao xác nhận tôi đã nhận được văn bản từ Tòa?',
    steps: [
      { bot: 'Để xác nhận đã nhận văn bản tố tụng:\n\n1. Vào **Vụ án của tôi** → chọn vụ án.\n2. Kéo xuống mục **Văn bản tố tụng**.\n3. Với từng văn bản trạng thái “Đã gửi”, bạn sẽ thấy nút **Xác nhận đã nhận** (hoặc **Đã đóng phí** nếu là giấy báo án phí).\n4. Bấm và xác nhận. Hệ thống sẽ ghi nhận thời điểm bạn đã biết nội dung văn bản.\n\nViệc này giúp Tòa nắm được tiến độ tống đạt và thời hạn của bạn (ví dụ thời hạn kháng cáo).' },
    ],
  },
]

function nextId() {
  return `msg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

function LitigantChatbot() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const scrollEndRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState<Message[]>(INIT_MESSAGES)
  const [input, setInput] = useState('')
  const [scenarioRunning, setScenarioRunning] = useState(false)

  useEffect(() => {
    scrollEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const appendBotReplies = (steps: ScenarioStep[], startIndex: number = 0) => {
    if (startIndex >= steps.length) {
      setScenarioRunning(false)
      return
    }
    const step = steps[startIndex]
    setMessages((prev) => [...prev, { id: nextId(), from: 'bot', text: step.bot }])
    if (startIndex + 1 < steps.length) {
      setTimeout(() => appendBotReplies(steps, startIndex + 1), 500)
    } else {
      setScenarioRunning(false)
    }
  }

  const runScenario = (scenario: Scenario) => {
    if (scenarioRunning) return
    setScenarioRunning(true)
    setMessages((prev) => [...prev, { id: nextId(), from: 'user', text: scenario.userMessage }])
    setTimeout(() => appendBotReplies(scenario.steps), 400)
  }

  const handleSend = () => {
    if (!input.trim()) return
    setMessages((prev) => [...prev, { id: nextId(), from: 'user', text: input.trim() }])
    setInput('')
    setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        {
          id: nextId(),
          from: 'bot',
          text: 'Cảm ơn bạn đã gửi câu hỏi. Trợ lý sẽ phản hồi trong giờ làm việc. Bạn cũng có thể chọn một chủ đề mẫu bên dưới để xem hướng dẫn nhanh.',
        },
      ])
    }, 600)
  }

  const panelWidth = isMobile ? '100%' : 380
  const panelHeight = isMobile ? '85vh' : 480

  return (
    <>
      {!open && (
        <Fab
          color="primary"
          aria-label="Chatbot hỗ trợ"
          onClick={() => setOpen(true)}
          sx={{
            position: 'fixed',
            bottom: { xs: 16, sm: 24 },
            right: { xs: 16, sm: 24 },
            zIndex: 1300,
            bgcolor: '#1A2B4A',
            '&:hover': { bgcolor: '#0f1e35' },
          }}
        >
          <ChatIcon />
        </Fab>
      )}

      {open && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: { xs: 0, sm: 24 },
            right: { xs: 0, sm: 24 },
            width: panelWidth,
            maxWidth: { xs: '100%', sm: 380 },
            height: panelHeight,
            maxHeight: { xs: '85vh', sm: 480 },
            zIndex: 1300,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: { xs: '16px 16px 0 0', sm: 2 },
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              p: 1.5,
              bgcolor: '#1A2B4A',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ChatIcon />
              <Typography variant="subtitle1" fontWeight={600}>
                Hỗ trợ đương sự
              </Typography>
            </Box>
            <IconButton size="small" color="inherit" onClick={() => setOpen(false)} aria-label="Đóng">
              <Close />
            </IconButton>
          </Box>

          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              bgcolor: 'grey.50',
            }}
          >
            {messages.map((m) => (
              <Box
                key={m.id}
                sx={{
                  alignSelf: m.from === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '85%',
                }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: m.from === 'user' ? 'primary.main' : 'background.paper',
                    color: m.from === 'user' ? 'primary.contrastText' : 'text.primary',
                  }}
                >
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{
                      fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                      lineHeight: 1.5,
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {m.text.split(/\*\*(.*?)\*\*/g).map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part))}
                  </Typography>
                </Paper>
              </Box>
            ))}
            <div ref={scrollEndRef} />
          </Box>

          <Box sx={{ px: 1.5, py: 1, borderTop: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
              Chọn chủ đề
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {SCENARIOS.map((s) => (
                <Chip
                  key={s.id}
                  label={s.label}
                  size="small"
                  onClick={() => runScenario(s)}
                  disabled={scenarioRunning}
                  sx={{
                    fontSize: '0.75rem',
                    mb: 0.5,
                    '&:hover': { bgcolor: 'primary.light', color: 'primary.contrastText' },
                  }}
                />
              ))}
            </Box>
          </Box>

          <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Nhập câu hỏi..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton color="primary" onClick={handleSend} disabled={!input.trim()} size="small" aria-label="Gửi">
                      <Send fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: { fontSize: { xs: '0.875rem', sm: '0.9375rem' } },
              }}
            />
          </Box>
        </Paper>
      )}
    </>
  )
}

export default LitigantChatbot
